import {
  Badge,
  Button,
  Select,
  SelectOptionElement,
} from '@odin-labs/components';
import React from 'react';
import {
  ReaderKindBadge,
  ReaderHealth,
  type ReaderDisplayFields,
} from 'siteLayout/readerPresentation';
import {
  Enforcement,
  Entry,
  EntrySide,
  Layout,
  OUTSIDE_LABEL,
  otherSide,
  sideCrossingLabel,
} from 'siteLayout';
import {
  enforcementHelp,
  enforcementLabels,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';

/** Option value for a side that faces the outside world rather than a zone. */
export const OUTSIDE_OPTION_VALUE = 'OUTSIDE';

export type SideReader = {
  id: string;
  name: string;
  portLabel: string;
  kind?: ReaderDisplayFields['kind'];
  health?: ReaderDisplayFields['health'];
  /** Named where it is placed, but not offered for placing. */
  missing?: boolean;
  /** No Avigilon reader is connected to it; offered for placing, and marked. */
  notConnected?: boolean;
};

function ReaderDetails({
  reader,
}: {
  reader: SideReader;
}): React.ReactElement | null {
  if (!reader.kind || !reader.health) return null;
  return (
    <>
      <ReaderKindBadge kind={reader.kind} />
      <span className="odin-block odin-text-xs odin-text-gray-500">
        <ReaderHealth
          reader={{
            kind: reader.kind,
            health: reader.health,
            missing: Boolean(reader.missing),
          }}
        />
      </span>
    </>
  );
}

const notConnectedBadge = (
  <Badge
    text="Not connected to Avigilon"
    color="yellow"
    className="odin-mt-1"
  />
);

export type EntrySideCardProps = {
  layout: Layout;
  entry: Entry;
  side: EntrySide;
  readers: SideReader[];
  unplacedPool: SideReader[];
  readOnly: boolean;
  editingDraft: boolean;
  pickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onSetFaces: (value: string) => void;
  onSetEnforcement: (enforcement: Enforcement) => void;
  onPlaceReader: (readerDeviceId: string) => void;
  onRemoveReader: (readerDeviceId: string) => void;
};

const classes = {
  card: 'odin-flex odin-flex-col odin-gap-3 odin-rounded-lg odin-border odin-border-gray-200 odin-p-4',
  label:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  chips: 'odin-flex odin-gap-2',
  help: 'odin-text-xs odin-text-gray-500',
  readers:
    'odin-flex odin-flex-col odin-gap-2 odin-border-t odin-border-gray-200 odin-pt-3',
  crossing: 'odin-font-mono odin-text-xs odin-text-gray-700',
  reader:
    'odin-flex odin-items-start odin-justify-between odin-gap-2 odin-rounded-md odin-border odin-border-gray-200 odin-px-3 odin-py-2',
  readerName: 'odin-text-sm odin-font-medium odin-text-gray-900',
  readerPort: 'odin-text-xs odin-text-gray-500',
  removeLink:
    'odin-text-xs odin-font-medium odin-text-gray-500 hover:odin-text-red-600',
  picker:
    'odin-flex odin-flex-col odin-gap-2 odin-rounded-md odin-bg-gray-50 odin-p-3',
  pickerHeader: 'odin-flex odin-items-center odin-justify-between',
  poolRow:
    'odin-flex odin-w-full odin-items-center odin-justify-between odin-gap-2 odin-rounded-md odin-border odin-border-gray-200 odin-bg-white odin-px-3 odin-py-2 odin-text-left hover:odin-border-gray-400',
  place: 'odin-text-xs odin-font-medium odin-text-odin-primary',
  empty: 'odin-text-xs odin-text-gray-500',
};

/** One face of an entry: what it faces, how crossings into it are treated, and the readers that admit into it. */
export function EntrySideCard({
  layout,
  entry,
  side,
  readers,
  unplacedPool,
  readOnly,
  editingDraft,
  pickerOpen,
  onOpenPicker,
  onClosePicker,
  onSetFaces,
  onSetEnforcement,
  onPlaceReader,
  onRemoveReader,
}: EntrySideCardProps): React.ReactElement {
  const other = otherSide(entry, side);
  const facesOptions: SelectOptionElement[] = [
    ...layout.zones
      .filter((zone) => zone.id !== other.zoneId)
      .map((zone) => ({ value: zone.id, label: zone.name })),
    ...(other.kind === 'OUTSIDE'
      ? []
      : [{ value: OUTSIDE_OPTION_VALUE, label: OUTSIDE_LABEL }]),
  ];
  const facesValue =
    side.kind === 'OUTSIDE'
      ? { value: OUTSIDE_OPTION_VALUE, label: OUTSIDE_LABEL }
      : facesOptions.find((option) => option.value === side.zoneId) ?? null;

  return (
    <div className={classes.card}>
      <p className={classes.label}>Side {side.position}</p>
      <Select
        name={`side-${side.position}-faces`}
        label="Faces"
        value={facesValue}
        options={facesOptions}
        isDisabled={readOnly}
        isSearchable={false}
        isClearable={false}
        onChange={(option: SelectOptionElement | null) => {
          if (option) onSetFaces(option.value);
        }}
      />
      <div>
        <p className={classes.label}>Enforcement</p>
        <div className={`${classes.chips} odin-mt-1`}>
          {readOnly ? (
            <Badge
              text={enforcementLabels[side.enforcement]}
              color={side.enforcement === 'ENFORCED' ? 'green' : 'gray'}
            />
          ) : (
            (['ENFORCED', 'IDENTIFY_ONLY'] as Enforcement[]).map((option) => (
              <Button
                key={option}
                text={enforcementLabels[option]}
                size="xs"
                theme={side.enforcement === option ? 'primary' : 'white'}
                onClick={() => onSetEnforcement(option)}
              />
            ))
          )}
        </div>
        <p className={`${classes.help} odin-mt-1`}>{enforcementHelp}</p>
      </div>

      <div className={classes.readers}>
        <p className={classes.label}>
          Readers admitting into this side · {readers.length}
        </p>
        <p className={classes.crossing}>
          Crossing: {sideCrossingLabel(layout, entry, side)}
        </p>
        {readers.map((reader) => (
          <div key={reader.id} className={classes.reader}>
            <div>
              <p className={classes.readerName}>{reader.name}</p>
              <p className={classes.readerPort}>{reader.portLabel}</p>
              <ReaderDetails reader={reader} />
              {reader.notConnected && notConnectedBadge}
            </div>
            {editingDraft && (
              <button
                type="button"
                className={classes.removeLink}
                onClick={() => onRemoveReader(reader.id)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {readers.length === 0 && (
          <p className={classes.empty}>
            No reader admits into this side. Enforcement here has no effect
            until one does.
          </p>
        )}
        {editingDraft && !pickerOpen && (
          <div>
            <Button
              text="Assign reader"
              size="xs"
              theme="white"
              onClick={onOpenPicker}
            />
          </div>
        )}
        {editingDraft && pickerOpen && (
          <div className={classes.picker}>
            <div className={classes.pickerHeader}>
              <span className={classes.label}>Readers available to place</span>
              <button
                type="button"
                className={classes.removeLink}
                onClick={onClosePicker}
              >
                Cancel
              </button>
            </div>
            {unplacedPool.map((reader) => (
              <button
                key={reader.id}
                type="button"
                className={classes.poolRow}
                onClick={() => onPlaceReader(reader.id)}
              >
                <span>
                  <span className={`${classes.readerName} odin-block`}>
                    {reader.name}
                  </span>
                  <span className={`${classes.readerPort} odin-block`}>
                    {reader.portLabel}
                  </span>
                  <ReaderDetails reader={reader} />
                  {reader.notConnected && notConnectedBadge}
                </span>
                <span className={classes.place}>Place</span>
              </button>
            ))}
            {unplacedPool.length === 0 && (
              <p className={classes.empty}>No unplaced readers on this site.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
